import React from 'react';

import { WatchQueryFetchPolicy } from '@apollo/client';

import { IBackendCartEntries, IHistoricalOrder } from '@rbi-ctg/menu';
import { IGetUserOrdersQueryVariables } from 'generated/graphql-gateway';
import { useFlag } from 'state/launchdarkly';
import { LaunchDarklyFlag } from 'state/launchdarkly/flags';
import { EnableMenuReorderingAvailabilityOverrideVariation } from 'state/launchdarkly/variations';
import { LoyaltyReward } from 'state/loyalty/types';
import { isOfferType } from 'state/order/utils';
import {
  CartEntryType,
  maybeCreateCartEntriesFromOffer,
  maybeCreateCartEntryFromReward,
} from 'utils/cart';

import { useLoyaltyRewardsList } from './use-loyalty-rewards-list';
import { useUserOrders } from './use-user-orders';

export const useReorderableOrders = (
  variables: IGetUserOrdersQueryVariables,
  fetchPolicy: WatchQueryFetchPolicy = 'cache-and-network'
) => {
  const { allRewards } = useLoyaltyRewardsList();
  const allRewardsMap = React.useMemo(() => {
    const dictionary: Record<string, LoyaltyReward> = {};
    (allRewards ?? []).forEach(item => (dictionary[item._id] = item));
    return dictionary;
  }, [allRewards]);

  // Temporary flag to disable reordering of items/combos based on Sanity IDs.
  // Should be removed once One Menu is live.
  const sanityIdsExcludedFromReordering =
    useFlag<EnableMenuReorderingAvailabilityOverrideVariation>(
      LaunchDarklyFlag.ENABLE_MENU_REORDERING_AVAILABILITY_OVERRIDE
    ) || [];

  const {
    orders,
    error,
    loading,
    count,
    resumeAfter,
    refetch,
    fetchMore,
    handleLoadMore,
    fetchingMore,
  } = useUserOrders(variables, fetchPolicy);

  const reorderableOrders = React.useMemo(
    () =>
      orders.reduce((ordersAccum: IHistoricalOrder[], order) => {
        const mappedReorderCartEntries = [];

        for (const entry of order?.cart?.cartEntries ?? []) {
          if (entry.isDonation || sanityIdsExcludedFromReordering.includes(entry.sanityId)) {
            continue;
          }

          let mappedEntries: IBackendCartEntries[] = [];
          // Offers or Rewards
          if (isOfferType(entry.type as CartEntryType)) {
            // Checks if a reorder cart entry is a reward,
            // then replaces the cart entry with the reward benefit item.
            const reward = allRewardsMap?.[entry.sanityId];
            if (reward) {
              mappedEntries = [maybeCreateCartEntryFromReward(entry, reward)];
            } else {
              // If it's not a reward, it's an Offer
              // Offer can't be re-applied, so we spread all its children instead
              mappedEntries = maybeCreateCartEntriesFromOffer(
                entry,
                sanityIdsExcludedFromReordering
              );
            }
          } else {
            // Regular entries are preserved as is
            mappedEntries = [entry];
          }
          // Append the mapped cartEntries
          mappedReorderCartEntries.push(...mappedEntries);
        }

        if (mappedReorderCartEntries?.length) {
          ordersAccum.push({
            ...order,
            cart: {
              ...order.cart,
              reorderCartEntries: mappedReorderCartEntries,
            },
          });
        }

        return ordersAccum;
      }, []),
    [orders, allRewardsMap]
  );

  return {
    error,
    loading,
    orders,
    reorderableOrders,
    count,
    resumeAfter,
    refetch,
    fetchMore,
    handleLoadMore,
    fetchingMore,
  };
};
